/**
 * The GLSL every part of an effect shares.
 *
 * Two chunks matter here.
 *
 * PROJECTION_CHUNK is the shared projection adapter, in the vertex shader. Every
 * mesh — ground decal, volume, particle — is authored in grid space and put on the
 * screen by this one function, so nothing can drift from the board or from anything
 * else in the effect. It is the same bilinear map the 2D canvas draws its cells
 * with, over four corner points that already carry the zoom, the pan and the morph.
 *
 * NOISE_CHUNK is sampled in grid space too, which is what keeps procedural detail
 * from restarting at every tile: the noise field belongs to the ground, not to the
 * quad being drawn, and two adjacent cells cannot show the same pattern because
 * they are not sampling the same coordinates.
 */

export const PROJECTION_CHUNK = /* glsl */`
uniform vec2 uCornerTL;
uniform vec2 uCornerTR;
uniform vec2 uCornerBR;
uniform vec2 uCornerBL;
uniform vec2 uGridSize;
uniform vec2 uViewport;
uniform float uCellPx;

vec2 gridToScreen(vec2 g){
  vec2 uv = g / uGridSize;
  vec2 top = mix(uCornerTL, uCornerTR, uv.x);
  vec2 bottom = mix(uCornerBL, uCornerBR, uv.x);
  return mix(top, bottom, uv.y);
}

/** Object transforms are grid-space transforms: a mesh can be placed, scaled and
 *  turned in cells like any other, and still land on the board through the one map. */
vec3 gridPosition(vec3 local){
  return (modelMatrix * vec4(local, 1.0)).xyz;
}

#ifndef BM3D
vec4 gridToClip(vec3 p){
  vec2 screen = gridToScreen(p.xz);
  screen.y -= p.y * uCellPx;
  return vec4(screen.x / uViewport.x * 2.0 - 1.0, 1.0 - screen.y / uViewport.y * 2.0, 0.0, 1.0);
}
#else
vec4 gridToClip(vec3 p){
  return projectionMatrix * viewMatrix * vec4(p, 1.0);
}
#endif
`;

export const WORLD_PROJECTION = Symbol('impact.worldProjection');

export function projectionOptions(uniforms) {
  if (uniforms && uniforms[WORLD_PROJECTION]) {
    return { defines: { BM3D: '' }, depthTest: true };
  }
  return { depthTest: false };
}

export const NOISE_CHUNK = /* glsl */`
uniform int uOctaves;

float hashNoise(vec2 p){
  p = fract(p * vec2(123.34, 345.45));
  p += dot(p, p + 34.345);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p){
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hashNoise(i);
  float b = hashNoise(i + vec2(1.0, 0.0));
  float c = hashNoise(i + vec2(0.0, 1.0));
  float d = hashNoise(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p){
  float value = 0.0;
  float amplitude = 0.55;
  for (int i = 0; i < 5; i++){
    if (i >= uOctaves) break;
    value += amplitude * valueNoise(p);
    p = p * 2.03 + vec2(17.1, 9.7);
    amplitude *= 0.5;
  }
  return value;
}

/**
 * Soft points of light scattered through a field — embers, frost glints, motes.
 *
 * The obvious way to write this is to quantise space and flash whole tiles on with
 * a step() over a hash of floor(p * 7.0). It is cheap and it is wrong: every
 * lit tile is a hard axis-aligned square that pops in and out, which on a grid board
 * reads as debris made of pixels rather than as fire — the artifact this replaces.
 *
 * Here each cell of the lattice seeds ONE point at a jittered position inside it,
 * and the point is a round falloff with a life that swells and fades. Nothing is
 * ever square, and nothing ever snaps on.
 */
float embers(vec2 p, float t, float radius){
  vec2 i = floor(p);
  vec2 f = fract(p);
  float glow = 0.0;
  for (int y = -1; y <= 1; y++){
    for (int x = -1; x <= 1; x++){
      vec2 g = vec2(float(x), float(y));
      vec2 seed = i + g;
      vec2 o = vec2(hashNoise(seed), hashNoise(seed + 11.3));
      float life = fract(hashNoise(seed + 3.7) * 7.0 + t);
      float d = length(g + o - f);
      glow += smoothstep(radius, 0.0, d) * sin(life * 3.14159);
    }
  }
  return clamp(glow, 0.0, 1.0);
}

float voronoiEdge(vec2 p){
  vec2 i = floor(p);
  vec2 f = fract(p);
  float first = 8.0;
  float second = 8.0;
  for (int y = -1; y <= 1; y++){
    for (int x = -1; x <= 1; x++){
      vec2 g = vec2(float(x), float(y));
      vec2 o = vec2(hashNoise(i + g), hashNoise(i + g + 7.7));
      float d = length(g + o - f);
      if (d < first) { second = first; first = d; }
      else if (d < second) { second = d; }
    }
  }
  return second - first;
}
`;

/** Positive inside the ideal shape. The GPU twin of `idealSignedDistance`. */
export const IDEAL_SHAPE_CHUNK = /* glsl */`
uniform int uFootprint;
uniform vec2 uOrigin;
uniform vec2 uDirection;
uniform float uRadius;
uniform float uHalfWidth;
uniform float uLength;
uniform float uHalfAngle;
uniform float uReach;

float idealSD(vec2 p){
  vec2 d = p - uOrigin;
  float distance = length(d);
  if (uFootprint == 0 || uFootprint == 2) return uRadius - distance;
  if (uFootprint == 3) return uHalfWidth - abs(distance - uRadius);
  if (uFootprint == 1){
    float t = clamp(dot(d, uDirection), 0.0, uLength);
    return uHalfWidth - length(d - uDirection * t);
  }
  if (uFootprint == 4){
    if (distance < 0.15) return uRadius;
    float angle = acos(clamp(dot(d, uDirection) / distance, -1.0, 1.0));
    return min(uRadius - distance, (uHalfAngle - angle) * max(distance, 0.001));
  }
  return 9.0;
}
`;

/**
 * The intent axis, shared by every stage.
 *
 * Violation says what an effect is made of; this says what it is doing with it.
 * Kept as its own chunk because all three materials need the same four numbers and
 * none of them should be re-deriving them from a skill type.
 */
export const INTENT_CHUNK = /* glsl */`
uniform int uIntent;
uniform vec3 uIntentBias;
uniform float uIntentAmount;
uniform float uLift;
uniform float uConverge;

/* Pull a violation colour toward what the action is for, without losing the material. */
vec3 intentTint(vec3 color){
  return mix(color, uIntentBias, uIntentAmount * 0.45);
}
`;

export const LIFECYCLE_CHUNK = /* glsl */`
uniform float uTime;
uniform float uActivation;
uniform float uBurst;
uniform float uDissolve;
uniform float uGrowth;
uniform float uChar;
uniform float uIntensity;
uniform float uAreaNorm;
uniform float uSeed;
uniform float uSince;
/*
 * How far this effect is being shown as a SHADOW of itself rather than as itself.
 *
 * An aim is a preview, and the honest preview of an effect is that effect with the
 * light taken out of it — the same material, the same shape, the same motion, sunk
 * toward its own deep end and stripped of its emitting core. Dimming alone does not
 * do it: a fireball at a third opacity is a faint fireball, still white-hot in the
 * middle, and reads as the thing having already happened somewhere behind the map.
 * Pulling the colour down to the material's own deep end reads as the thing NOT
 * having happened yet.
 *
 * Lives here rather than in each material because it applies to every layer an effect
 * is made of, and a shadow that reaches the ground but not the motes above it is not
 * a shadow of anything.
 */
uniform float uShadow;

/** The material, unlit. Applied last, after every mode has had its say. */
vec3 shadowed(vec3 lit, vec3 deep){
  if (uShadow <= 0.0) return lit;
  /*
   * Sunk toward the material AND de-emitted. The mix alone is not enough for the
   * modes that add unbiased white on top of their ramp — Energy's filaments and
   * Light's body both do — because white added after the mix survives it, and the
   * aim comes out pale rather than shadowed. Taking a third of the level off
   * afterwards puts the emission back under the material.
   */
  return mix(lit, deep, uShadow * 0.55) * (1.0 - uShadow * 0.32);
}
`;

/**
 * Emitted light, as a ramp rather than as a colour.
 *
 * Anything that is supposed to look hot has to run white in the middle and cool
 * outward — a sprite filled with one saturated colour reads as a decal however
 * bright it is, because nothing real emits at a single wavelength across its whole
 * body. `emberRamp` is that ramp: 0 is the cooled tail, 1 is the emitting core.
 *
 * The overshoot past 1 is deliberate. On an additive material a core that stops at
 * white cannot bloom, and the difference between "a bright dot" and "something too
 * bright to look at" is entirely in the values above one.
 */
export const EMISSION_CHUNK = /* glsl */`
uniform vec3 uColorA;
uniform vec3 uColorB;
uniform vec3 uColorCore;

vec3 emberRamp(float heat){
  float h = clamp(heat, 0.0, 1.0);
  vec3 cool = mix(uColorB, uColorA, smoothstep(0.0, 0.55, h));
  return mix(cool, uColorCore, smoothstep(0.55, 1.0, h)) * (1.0 + smoothstep(0.8, 1.0, h) * 0.9);
}
`;

/**
 * A particle with somewhere to be going.
 *
 * Integrated in closed form rather than stepped on the CPU: with linear drag the
 * path has an exact solution, so every particle's whole flight is a function of one
 * clock and its own constants. Nothing is uploaded per frame, the budget survives
 * the low tier, and — the part that matters visually — the motion is a real
 * ballistic curve with a decelerating head, not a position lerp pretending to be one.
 *
 *   p(t) = p0 + v0 (1 - e^-kt)/k + g t² / 2
 */
export const PARTICLE_PATH_CHUNK = /* glsl */`
vec3 dragPath(vec3 p0, vec3 v0, float k, float gravity, float t){
  float decay = (k < 0.001) ? t : (1.0 - exp(-k * t)) / k;
  vec3 p = p0 + v0 * decay;
  p.y -= gravity * t * t * 0.5;
  return p;
}
`;
